import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, Rectangle, CircleMarker, Popup, useMap, useMapEvents } from "react-leaflet";
import { formatEuro } from "@/lib/api";

// yield color scale: cyan (low) → lime (high)
function yieldColor(y) {
  if (y == null) return "#3f3f46";
  // 2% → 8% typical
  const t = Math.max(0, Math.min(1, (y - 2) / 6));
  // interpolate hue from cyan (190) to lime (85)
  const hue = 190 - t * 105;
  const sat = 70 + t * 20;
  const light = 45 + t * 10;
  return `hsl(${hue}, ${sat}%, ${light}%)`;
}

function FitBounds({ bounds }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds([[bounds.lat_min, bounds.lng_min], [bounds.lat_max, bounds.lng_max]]);
    }
  }, [bounds, map]);
  return null;
}

function BBoxDrawer({ enabled, onDraw }) {
  const startRef = useRef(null);
  const map = useMap();
  useEffect(() => {
    if (enabled) map.getContainer().style.cursor = "crosshair";
    else map.getContainer().style.cursor = "";
    return () => { map.getContainer().style.cursor = ""; };
  }, [enabled, map]);
  useMapEvents({
    click(e) {
      if (!enabled) return;
      if (!startRef.current) {
        startRef.current = e.latlng;
      } else {
        const s = startRef.current, x = e.latlng;
        const bbox = {
          lat_min: Math.min(s.lat, x.lat),
          lat_max: Math.max(s.lat, x.lat),
          lng_min: Math.min(s.lng, x.lng),
          lng_max: Math.max(s.lng, x.lng),
        };
        startRef.current = null;
        onDraw?.(bbox);
      }
    },
  });
  return null;
}

export default function MapView({ grid, listings = [], auctions = [], showListings = true, showAuctions = true, onCellClick, drawMode = false, onBBoxDraw, drawnBBox = null }) {
  const center = grid?.center || [45.4642, 9.19];

  const bounds = grid?.bounds;

  const cellRects = useMemo(() => {
    if (!grid?.cells) return null;
    return grid.cells.map((c) => (
      <Rectangle
        key={`${c.i}-${c.j}`}
        bounds={c.bounds}
        pathOptions={{
          color: yieldColor(c.yield_pct),
          weight: 1,
          fillColor: yieldColor(c.yield_pct),
          fillOpacity: c.yield_pct ? 0.42 : 0.10,
        }}
        eventHandlers={onCellClick ? { click: () => onCellClick(c) } : undefined}
      >
        <Popup>
          <div className="font-mono-data text-xs space-y-1 min-w-[180px]">
            <div className="text-lime-400 font-bold">CELLA #{c.i}-{c.j}</div>
            <div className="flex justify-between"><span className="text-zinc-400">€/m² vendita</span><span>{c.avg_price_sqm ? formatEuro(c.avg_price_sqm) : "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">€/m² affitto</span><span>{c.avg_rent_sqm ?? "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">Rendimento</span><span className="text-lime-400 font-bold">{c.yield_pct ? `${c.yield_pct}%` : "—"}</span></div>
            <div className="flex justify-between"><span className="text-zinc-400">N. immobili</span><span>{c.n_listings}</span></div>
          </div>
        </Popup>
      </Rectangle>
    ));
  }, [grid, onCellClick]);

  return (
    <MapContainer
      center={center}
      zoom={12}
      className="h-full w-full"
      data-testid="map-view"
      scrollWheelZoom
    >
      <TileLayer
        url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        attribution='&copy; OpenStreetMap &copy; CARTO'
      />
      {bounds && <FitBounds bounds={bounds} />}
      <BBoxDrawer enabled={drawMode} onDraw={onBBoxDraw} />
      {drawnBBox && (
        <Rectangle
          bounds={[[drawnBBox.lat_min, drawnBBox.lng_min], [drawnBBox.lat_max, drawnBBox.lng_max]]}
          pathOptions={{ color: "#A3E635", weight: 2, dashArray: "6,4", fillOpacity: 0.05 }}
        />
      )}
      {cellRects}

      {showListings && listings.map((l) => (
        <CircleMarker
          key={l.id}
          center={[l.lat, l.lng]}
          radius={3}
          pathOptions={{
            color: l.type === "vendita" ? "#FAFAFA" : "#06B6D4",
            fillColor: l.type === "vendita" ? "#FAFAFA" : "#06B6D4",
            fillOpacity: 0.85,
            weight: 0,
          }}
        >
          <Popup>
            <div className="font-mono-data text-xs space-y-1 min-w-[180px]">
              <div className="text-lime-400 font-bold uppercase">{l.type}</div>
              <div>{l.title}</div>
              <div className="text-zinc-400">{l.address}</div>
              <div className="flex justify-between"><span className="text-zinc-400">Prezzo</span><span className="font-bold">{formatEuro(l.price)}{l.type === "affitto" ? "/mese" : ""}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Superficie</span><span>{l.sqm} m²</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">€/m²</span><span>{l.price_per_sqm}</span></div>
            </div>
          </Popup>
        </CircleMarker>
      ))}

      {showAuctions && auctions.map((a) => (
        <CircleMarker
          key={a.id}
          center={[a.lat, a.lng]}
          radius={5}
          pathOptions={{ color: "#F59E0B", fillColor: "#F59E0B", fillOpacity: 0.9, weight: 1 }}
        >
          <Popup>
            <div className="font-mono-data text-xs space-y-1 min-w-[200px]">
              <div className="text-amber-400 font-bold uppercase">ASTA · -{a.discount_pct}%</div>
              <div>{a.title}</div>
              <div className="text-zinc-400">{a.court} · {a.procedure_number}</div>
              <div className="flex justify-between"><span className="text-zinc-400">Base d'asta</span><span className="font-bold">{formatEuro(a.base_price)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Valore mercato</span><span>{formatEuro(a.market_price)}</span></div>
              <div className="flex justify-between"><span className="text-zinc-400">Data</span><span>{a.auction_date}</span></div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </MapContainer>
  );
}
